/**
 * The "From … To … aligner" range on a treatment step.
 *
 * Both the create and the update screens ask for it, and both used to
 * ask with a bare <Input type="number">, which accepts anything the
 * spinner can be talked into: nothing at all, -4, 2.5, 000012, and
 * 1e21. All of it was stringified straight into treatment_steps, so a
 * plan could be saved describing aligners that cannot exist.
 *
 * A step number is a whole aligner in a sequence, so the only valid
 * value is a non-negative integer (the first step in a sequence starts
 * at 0), and the pair has to read forwards.
 */

export const ALIGNER_MIN = 0;
export const ALIGNER_MAX = 500;

const WHOLE_NUMBER = /^\d+$/;

/**
 * One end of the range. Returns the message to show, or null when the
 * value is fine.
 *
 * `label` names the field so the message says which end is wrong —
 * "From must be a whole number" rather than a message the reader has to
 * match up to a box themselves.
 */
export const alignerNumberError = (value, label) => {
  const raw = value === null || value === undefined ? '' : String(value).trim();

  if (raw === '') {
    return `${label} is required.`;
  }

  // A type="number" input hands back "" for text it could not parse, so
  // anything still here that is not digits is a sign, a decimal point or
  // an exponent — all of which this field has no meaning for.
  if (! WHOLE_NUMBER.test(raw)) {
    return `${label} must be a whole number — no decimals, signs or spaces.`;
  }

  // "007" and "1" are the same aligner written two ways; keeping both
  // would let the same step be saved under two different strings.
  if (raw.length > 1 && raw.startsWith('0')) {
    return `${label} cannot start with a zero.`;
  }

  const number = Number(raw);

  if (number < ALIGNER_MIN || number > ALIGNER_MAX) {
    return `${label} must be between ${ALIGNER_MIN} and ${ALIGNER_MAX}.`;
  }

  return null;
};

/**
 * Both ends of one step, plus the rule that connects them.
 *
 * Returns `{ from, to }` where each is a message or null — the shape the
 * screens hang under the two inputs.
 */
export const treatmentStepErrors = (step) => {
  const source = step || {};
  const from = alignerNumberError(source.from, 'From');
  const to = alignerNumberError(source.to, 'To');

  if (! from && ! to && Number(source.to) < Number(source.from)) {
    return { from: null, to: 'To must be the same as From or later.' };
  }

  return { from, to };
};

/** The first thing wrong across every step, or null when all of them pass. */
export const firstTreatmentStepError = (steps) => {
  const list = Array.isArray(steps) ? steps : [];

  for (let index = 0; index < list.length; index += 1) {
    const { from, to } = treatmentStepErrors(list[index]);
    if (from || to) {
      return `Treatment step ${index + 1}: ${from || to}`;
    }
  }

  return null;
};
